import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Center, Loader, Stack, Text } from "@mantine/core";

import { handleCallback } from "@/auth/flow";

/**
 * IAM redirect-back handler. Validates `state`, exchanges `code` for tokens
 * via the backend, then navigates to the `next` destination stored in
 * sessionStorage at login init.
 *
 * Renders an inline error alert on exchange failure rather than redirecting
 * — surfacing the failure mode is more useful than silently bouncing the
 * user back to /login (which would re-trigger the same failed exchange).
 */
export function CallbackPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { next } = await handleCallback(searchParams);
        if (!cancelled) {
          navigate(next, { replace: true });
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  if (error !== null) {
    return (
      <Center h="100vh">
        <Alert color="red" title="Sign-in failed" maw={480}>
          {error}
        </Alert>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Stack align="center" gap="md">
        <Loader />
        <Text c="dimmed">Completing sign-in...</Text>
      </Stack>
    </Center>
  );
}
